import { useEffect, useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/Product/ProductCard";
import FilterSheet from "@/components/Common/FilterSheet";
import InlineAd, { injectInlineAds } from "@/components/Common/InlineAd";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Search() {
  const [sp, setSp] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [favIds, setFavIds] = useState([]);
  const [inlineAds, setInlineAds] = useState([]);
  const { user } = useAuth();
  const nav = useNavigate();

  const filters = useMemo(() => {
    const f = {};
    for (const [k, v] of sp.entries()) {
      if (k === "open_filters") continue;
      if (!v) continue;
      if (["wholesale", "has_video", "verified_only"].includes(k)) f[k] = v === "1" || v === "true";
      else if (["min_price", "max_price"].includes(k)) f[k] = parseInt(v);
      else f[k] = v;
    }
    return f;
  }, [sp]);

  const autoOpenFilters = sp.get("open_filters") === "1";

  const handleFilterSheetChange = (isOpen) => {
    if (!isOpen && autoOpenFilters) {
      const next = new URLSearchParams(sp);
      next.delete("open_filters");
      setSp(next, { replace: true });
    }
  };

  useEffect(() => {
    api.get("/meta/categories").then(r => setCategories(r.data || []));
    api.get("/ads/inline", { params: { placement: "search" } }).then(r => setInlineAds(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v === true) params[k] = true;
      else if (v) params[k] = v;
    });
    api.get("/products", { params }).then(r => setProducts(r.data || [])).finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => {
    if (!user) { setFavIds([]); return; }
    api.get("/favorites/ids").then(r => setFavIds(r.data?.product_ids || [])).catch(() => {});
  }, [user]);

  const setFilters = (obj) => {
    const next = new URLSearchParams();
    Object.entries(obj).forEach(([k, v]) => {
      if (v === undefined || v === "" || v === false) return;
      next.set(k, v === true ? "1" : String(v));
    });
    if (filters.q) next.set("q", filters.q);
    setSp(next);
  };

  const toggleFav = async (id) => {
    if (!user) { toast.error("Connectez-vous"); nav("/connexion"); return; }
    const { data } = await api.post("/favorites/toggle", { target_id: id, target_type: "product" });
    setFavIds((ids) => data.favorited ? [...ids, id] : ids.filter(x => x !== id));
  };

  return (
    <div className="px-3 sm:px-6 py-4 max-w-7xl mx-auto">
      <div className="flex items-center gap-2 mb-4">
        <div className="font-bold text-[#1A1918]">
          {filters.q ? `Résultats pour « ${filters.q} »` : "Rechercher"}
          <span className="ml-2 text-sm text-[#73706B] font-normal">{products.length} produit{products.length > 1 ? "s" : ""}</span>
        </div>
        <div className="ml-auto"><FilterSheet filters={filters} setFilters={setFilters} categories={categories} defaultOpen={autoOpenFilters} onOpenChange={handleFilterSheetChange} /></div>
      </div>

      {loading && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="aspect-square rounded-xl shimmer" />
          ))}
        </div>
      )}

      {!loading && products.length === 0 && (
        <div className="py-16 text-center">
          <div className="text-[#73706B] mb-1">Aucun produit trouvé</div>
          <div className="text-xs text-[#73706B]/70">Essayez d'ajuster vos filtres</div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
        {injectInlineAds(products, inlineAds).map((it, i) =>
          it?.__ad ? (
            <InlineAd key={`ad-${i}-${it.ad.id}`} ad={it.ad} />
          ) : (
            <ProductCard key={it.id} product={it} onFavorite={toggleFav} isFav={favIds.includes(it.id)} />
          )
        )}
      </div>
    </div>
  );
}
